import first from "lodash-es/first";
import uniq from "lodash-es/uniq";

import { useCompanyInfo } from "../../core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "../../core/model/model";

import ExternalSystem = m.Entities.Bank.ExternalSystem;

export const FIXME_SVEA_MULTIPLE_ACCOUNTS = <TReturn extends unknown[]>({
	original,
	patchBeforeWeImplementSelectAccount,
}: {
	original: () => TReturn;
	patchBeforeWeImplementSelectAccount: (originalReturn: TReturn) => TReturn;
}) => {
	const originalReturn = original();
	return patchBeforeWeImplementSelectAccount(originalReturn);
};

export const isExternalSystemBba = (externalSystem: ExternalSystem) =>
	externalSystem === ExternalSystem.Mir || externalSystem === ExternalSystem.Svea;

export const isMoneyAccountBba = (moneyAccount: m.Bokio.Bank.Contract.Dtos.MoneyAccountWithInfo) => {
	return moneyAccount.Connection && isExternalSystemBba(moneyAccount.Connection.Bank.ExternalSystem);
};

export const getActiveBankConnectionInfo = (
	// CompanyInfo is typed as possible to be undefined
	// to be backward compatible with the web app's behaviour
	// that companyInfo from the store can be undefined when loading.
	companyInfo: m.Settings.Controllers.CompanyInfoForView | undefined,
) => {
	const activeBankConnections = companyInfo?.ActiveBankConnections ?? [];

	const hasActiveConnection = (externalSystem: ExternalSystem) =>
		activeBankConnections.some(c => c.ExternalSystem === externalSystem);
	const hasChosenBookkeepingAccount = (externalSystem: ExternalSystem) =>
		activeBankConnections.some(c => c.ExternalSystem === externalSystem && c.BookkeepingAccount);
	const anyConnectionSupportsPayment = activeBankConnections.some(c => c.BankConfiguration.SupportsPayment);
	const anyConnectionSupportsCard = activeBankConnections.some(c => c.BankConfiguration.SupportsCard);
	const anyConnectionSupportsBankInbox = activeBankConnections.some(c => c.BankConfiguration.SupportsBankInbox);

	const bbaPaymentAccounts = FIXME_SVEA_MULTIPLE_ACCOUNTS({
		original: () =>
			companyInfo?.ActiveBankConnections?.filter(
				c =>
					isExternalSystemBba(c.ExternalSystem) &&
					// Business Account means that it also has to have payment support
					c.BankConfiguration.SupportsPayment,
			) ?? [],
		patchBeforeWeImplementSelectAccount: accounts => {
			return accounts.filter(ba => ba.BankAccountType === m.Entities.Bank.BankAccountType.Depository);
		},
	});

	if (bbaPaymentAccounts.length > 1) {
		throw new Error("Multiple BBA accounts (e.g. having both Mir and Svea at the same time) is not supported yet.");
	}

	const activeBusinessAccountExternalSystems = uniq(bbaPaymentAccounts.map(a => a.ExternalSystem));
	if (activeBusinessAccountExternalSystems.length > 1) {
		throw new Error(
			"Multiple BBA external systems (e.g. having both Mir and Svea at the same time) is not supported yet.",
		);
	}
	const activeBusinessAccountExternalSystem = first(activeBusinessAccountExternalSystems);

	const activeBusinessAccount = first(bbaPaymentAccounts);
	const hasActiveBusinessAccount = !!activeBusinessAccount;
	const activeBusinessAccountExternalSystemHasConsent = activeBusinessAccount?.HasConsent;

	const hasActiveBusinessAccountWithExternalSystem = (externalSystem: ExternalSystem) =>
		activeBusinessAccount?.ExternalSystem === externalSystem;

	const activeBusinessAccountSupportsSendingInternationalPayments =
		activeBusinessAccount?.BankConfiguration.SupportsInternationalPayment ?? false;
	const activeBusinessAccountSupportsReceivingInternationalPayments =
		activeBusinessAccount?.BankConfiguration.SupportsReceivingInternationalPayments ?? false;
	const activeBusinessAccountSupportsOutgoingInternationalPaymentsType = (type: m.Entities.Teller.TellerPaymentType) =>
		activeBusinessAccount?.BankConfiguration.SupportedInternationalOutgoingPaymentTypes.includes(type);

	const anyConnectionSupportManuallyTriggeredSync = activeBankConnections.some(
		c => c.BankConfiguration.AllowsManuallyTriggeredSync && c.BookkeepingAccount !== 0,
	);

	const shouldBlockBankServices =
		activeBusinessAccount?.KycInformation?.RequiredAction === m.Bokio.Common.Helpers.SveaKycAction.BlockPayments;

	const hasSveaNonConsentCompanyWithEmptyBankAccount =
		companyInfo?.HasSveaNonConsentCompanyWithEmptyBankAccount ?? false;

	const hasActiveSavingsAccount = activeBankConnections.some(
		c => c.BankAccountType === m.Entities.Bank.BankAccountType.Saving && c.BookkeepingAccount,
	);

	const activeBankAccountsWithPaymentSupport = activeBankConnections?.filter(
		c => isExternalSystemBba(c.ExternalSystem) && c.BankConfiguration.SupportsPayment,
	);

	const isCompanyTypeEligibleForBusinessAccountOnboarding = (companyType: m.Entities.CompanyType) => {
		switch (companyType) {
			case m.Entities.CompanyType.LimitedCompany:
			case m.Entities.CompanyType.Proprietorship:
			case m.Entities.CompanyType.Partnership:
			case m.Entities.CompanyType.LimitedPartnership:
			case m.Entities.CompanyType.Cooperative:
				return true;
			case m.Entities.CompanyType.NonProfitAssociation:
			case m.Entities.CompanyType.CommunityAssociation:
			case m.Entities.CompanyType.ReligiousCommunity:
			case m.Entities.CompanyType.NotSet:
				return false;
		}
	};

	const companyTypeSupportsBusinessAccountOnboarding = () => {
		const companyType = companyInfo?.CompanyType;
		if (!companyType) {
			return false;
		}
		return isCompanyTypeEligibleForBusinessAccountOnboarding(companyType);
	};

	return {
		hasActiveConnection,
		hasChosenBookkeepingAccount,
		anyConnectionSupportsPayment,
		anyConnectionSupportsCard,
		activeBusinessAccount,
		hasActiveBusinessAccount,
		hasActiveBusinessAccountWithExternalSystem,
		activeBusinessAccountSupportsSendingInternationalPayments,
		activeBusinessAccountSupportsReceivingInternationalPayments,
		activeBusinessAccountSupportsOutgoingInternationalPaymentsType,
		anyConnectionSupportManuallyTriggeredSync,
		anyConnectionSupportsBankInbox,
		activeBusinessAccountExternalSystem,
		activeBusinessAccountExternalSystemHasConsent,
		shouldBlockBankServices,
		hasSveaNonConsentCompanyWithEmptyBankAccount,
		hasActiveSavingsAccount: hasActiveSavingsAccount,
		activeBankAccountsWithPaymentSupport,
		companyTypeSupportsBusinessAccountOnboarding,
		isCompanyTypeEligibleForBusinessAccountOnboarding,
	};
};

export const useActiveBankConnection = () => {
	const { companyInfo, reloadCompanyInfo } = useCompanyInfo();
	const activeBankConnectionInfo = getActiveBankConnectionInfo(companyInfo);
	return {
		...activeBankConnectionInfo,
		refresh: reloadCompanyInfo,
	};
};
